import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { useReducedMotion } from 'framer-motion';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { tokens } from './tokens';
import { TIMING } from './animationConfig';

/**
 * Hardened markdown renderer for assistant prose. The data-safety boundary forbids rendered
 * conversational text from issuing a network request or navigating away, so:
 *   - `img` is rendered inert (the alt text only) — never an <img> that fetches a URL.
 *   - `a` is rendered as plain inline text (no href) — no external navigation.
 *   - no `rehype-raw`, so embedded HTML never reaches the DOM.
 */
const baseComponents = {
  // Drop the image entirely; surface its alt text so the prose still reads.
  img: ({ alt }: any) => <span>{alt || ''}</span>,
  // Strip the href: the link text stays, but it can't navigate or be a beacon target.
  a: ({ children }: any) => <span>{children}</span>,
} as const;

// While streaming, each newly arrived word eases in (fade + a touch of blur clearing) instead of snapping
// to full ink. Each word animates exactly ONCE: a word at a given document position keeps a stable key
// (its index) across token re-renders, so React reuses its DOM node and only the freshly appended word
// mounts and runs the keyframe — settled words don't re-animate, and a word that grows char-by-char while
// streaming keeps its node (no re-fire).
const wordIn = keyframes`
  from { opacity: 0; filter: blur(4px); }
  to   { opacity: 1; filter: blur(0); }
`;
const Word = styled.span`
  display: inline;
  animation: ${wordIn} ${TIMING.wordFadeMs}ms cubic-bezier(0.16, 1, 0.3, 1) both;
`;

// Split a leaf text string into one keyed span per word, keeping the whitespace that follows a word
// attached to it so markdown spacing survives. `ctx.i` is a render-stable, document-order word counter:
// reset to 0 at the start of each render, it gives a word the same key — and therefore the same DOM
// node — as later tokens arrive.
function splitWords(text: string, ctx: { i: number }): React.ReactNode[] {
  const chunks = text.match(/\s*\S+\s*/g);
  if (!chunks) return text ? [text] : []; // pure whitespace between inline nodes — keep it verbatim
  return chunks.map((chunk) => <Word key={`w${ctx.i++}`}>{chunk}</Word>);
}

// Wrap only the DIRECT string children of a text-bearing element; nested elements (e.g. <strong>) are
// left for their own override to wrap, so a word is never double-wrapped.
function wrapStrings(children: React.ReactNode, ctx: { i: number }): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  React.Children.forEach(children, (child) => {
    if (typeof child === 'string') out.push(...splitWords(child, ctx));
    else out.push(child);
  });
  return out;
}

// Component overrides that wrap leaf text in animated word spans, sharing one render-scoped counter.
// `node` (react-markdown's hast node) is stripped so it never lands on the DOM element as an attribute.
function streamComponents(ctx: { i: number }) {
  const wrap =
    (Tag: keyof JSX.IntrinsicElements) =>
    ({ node, children, ...props }: any) =>
      <Tag {...props}>{wrapStrings(children, ctx)}</Tag>;
  return {
    ...baseComponents,
    p: wrap('p'),
    li: wrap('li'),
    strong: wrap('strong'),
    em: wrap('em'),
    h1: wrap('h1'),
    h2: wrap('h2'),
    h3: wrap('h3'),
    blockquote: wrap('blockquote'),
  };
}

/**
 * Renders assistant prose as markdown. Rendering is memoized on the text so that, in a transcript,
 * only the actively streaming bubble reparses on each token — settled (done) turns don't re-render.
 *
 * `stream`: while the turn is streaming, words fade in as they arrive (see Word). Settled prose and
 * reduced-motion render plainly — no per-word animation.
 */
export function Markdown({ text, stream = false }: { text: string; stream?: boolean }) {
  const reduceMotion = useReducedMotion();
  const animate = stream && !reduceMotion;
  // Stable counter object so the word-wrapping component functions can keep a stable identity (a new
  // identity each render would remount the whole subtree and re-fire every word's fade).
  const ctxRef = React.useRef({ i: 0 });
  // Reset the document-order word counter for this render; the word spans increment it as React renders
  // them (after this function returns), so indices stay consistent across tokens. Harmless when the memo
  // below returns its cached tree (no word spans re-render).
  ctxRef.current.i = 0;

  const components = useMemo(() => (animate ? streamComponents(ctxRef.current) : baseComponents), [animate]);

  return useMemo(
    () => (
      <div
        style={{
          fontFamily: tokens.fontFamily,
          color: tokens.text.primary,
          fontSize: 15,
          lineHeight: 1.6,
        }}
      >
        <ReactMarkdown components={components}>{text}</ReactMarkdown>
      </div>
    ),
    [text, components],
  );
}
